import React from "react";

import { RotateCw } from "lucide-react";

import { ACCENT, Modal, PointList } from "./Chrome";
import { useAppSelector } from "../../../Store/Hooks";
import type { XFactorDigestData } from "../../../Types/XFactorBriefing";
import { DEFAULT_HEALTH_SCORE_THRESHOLDS } from "../../../Utils/HealthThresholds";
import { bandOf, buildBriefingCards } from "../lib/derive";

const VALUE_TONE = {
  strong: "bg-green-50 text-green-700 ring-green-200",
  moderate: "bg-amber-50 text-amber-700 ring-amber-200",
  weak: "bg-red-50 text-red-700 ring-red-200",
  unknown: "bg-gray-50 text-gray-400 ring-gray-200",
};

/**
 * The per-section score cards. Each flips to its narrative — the score face is
 * the scan view, the back is the read view.
 */
const BriefingCardsModal: React.FC<{
  open: boolean;
  onClose: () => void;
  accountName: string;
  digestDate?: string | null;
  data?: XFactorDigestData | null;
}> = ({ open, onClose, accountName, digestDate, data }) => {
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const cards = React.useMemo(() => buildBriefingCards(data), [data]);
  const [flipped, setFlipped] = React.useState<Record<string, boolean>>({});
  const [face, setFace] = React.useState<Record<string, "summary" | "recs">>(
    {}
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <span>
          Public data briefing cards —{" "}
          <span style={{ color: ACCENT }}>{accountName}</span>
        </span>
      }
      subtitle={digestDate}
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
        {cards.map(card => {
          const isFlipped = Boolean(flipped[card.sectionId]);
          const side = face[card.sectionId] || "summary";

          return (
            <div
              key={card.sectionId}
              className="flex min-h-[260px] flex-col rounded-lg border border-gray-200 bg-white p-3"
            >
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold" style={{ color: ACCENT }}>
                  {card.label}
                </h3>
                <button
                  type="button"
                  onClick={() =>
                    setFlipped(f => ({
                      ...f,
                      [card.sectionId]: !f[card.sectionId],
                    }))
                  }
                  aria-label={isFlipped ? "Show scores" : "Show narrative"}
                  title={isFlipped ? "Show scores" : "Show narrative"}
                  className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                >
                  <RotateCw size={13} />
                </button>
              </div>

              {card.missing ? (
                <p className="text-xs italic text-gray-400">
                  Not present in this briefing.
                </p>
              ) : isFlipped ? (
                <div className="flex min-h-0 flex-1 flex-col">
                  <div className="mb-2 flex gap-1">
                    {(["summary", "recs"] as const).map(s => (
                      <button
                        key={s}
                        type="button"
                        onClick={() =>
                          setFace(f => ({ ...f, [card.sectionId]: s }))
                        }
                        className={`rounded px-2 py-0.5 text-[11px] font-medium transition-colors ${
                          side === s
                            ? "bg-purple-50 text-[#ad46ff]"
                            : "text-gray-500 hover:bg-gray-100"
                        }`}
                      >
                        {s === "summary" ? "Summary" : "Recommendations"}
                      </button>
                    ))}
                  </div>
                  <div className="min-h-0 flex-1 overflow-y-auto">
                    {side === "summary" ? (
                      <>
                        <p className="text-xs leading-snug text-gray-700">
                          {card.synopsis || "No synopsis in this section."}
                        </p>
                        {card.risksAndOpportunities ? (
                          <>
                            <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                              Risks &amp; opportunities brief
                            </p>
                            <p className="mt-0.5 text-xs leading-snug text-gray-600">
                              {card.risksAndOpportunities}
                            </p>
                          </>
                        ) : null}
                      </>
                    ) : (
                      <PointList
                        points={card.recommendations}
                        empty="No recommendations in this section."
                      />
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex-1">
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Scores
                  </p>
                  <dl className="space-y-1.5">
                    {card.scores.map(s => {
                      const band = bandOf(s.value, bhThresholds);
                      return (
                        <div key={s.label}>
                          <dt className="text-[10px] font-medium uppercase tracking-wide text-gray-500">
                            {s.label}
                          </dt>
                          <dd
                            className={`mt-0.5 inline-flex min-w-[2.25rem] justify-center rounded px-1.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${VALUE_TONE[band]}`}
                          >
                            {s.value == null ? "—" : Math.round(s.value)}
                          </dd>
                        </div>
                      );
                    })}
                  </dl>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default BriefingCardsModal;
